/**
 * Enhanced GemsService - Dynamic Gem Type Processing
 * Implements Open/Closed Principle - open for extension, closed for modification
 */
import { gemTypeRegistry } from './GemTypeRegistry';
import { FIELD_MAPPINGS } from '@/config/defaultGemTypes';
export class GemsServiceError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.cause = cause;
        this.name = 'GemsServiceError';
    }
}
export class GemsService {
    static DEBUG_PREFIX = '[GemsService]';
    /**
     * Extract and process gems data from session object
     * @param session - Session object containing gems data
     * @returns Processed session gems or null
     */
    static extractGemsFromSession(session) {
        try {
            if (!session) {
                return null;
            }
            if (!session.gems) {
                return null;
            }
            const result = this.processSessionGems(session.gems);
            // Register dynamic gem types with the registry
            if (result && Object.keys(result).length > 0) {
                try {
                    gemTypeRegistry.registerSessionTypes(result);
                }
                catch (registryError) {
                    console.warn(`${this.DEBUG_PREFIX} Failed to register gem types:`, registryError);
                }
            }
            return result;
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error extracting gems:`, error);
            throw new GemsServiceError('Failed to extract gems from session', error);
        }
    }
    /**
     * Process and validate session gems data
     * @param rawData - Raw gems data from backend
     * @returns Processed session gems
     */
    static processSessionGems(rawData) {
        try {
            if (!rawData || typeof rawData !== 'object') {
                console.warn(`${this.DEBUG_PREFIX} Invalid gems data received:`, rawData);
                return {};
            }
            // Handle nested GEMS structure
            const gemsData = rawData.GEMS || rawData;
            if (!gemsData || typeof gemsData !== 'object') {
                console.warn(`${this.DEBUG_PREFIX} No GEMS data found:`, rawData);
                return {};
            }
            const processedGems = {};
            Object.entries(gemsData).forEach(([typeName, items]) => {
                if (!Array.isArray(items)) {
                    console.warn(`${this.DEBUG_PREFIX} Invalid items for type ${typeName}:`, items);
                    return;
                }
                // Process and validate each item
                const processedItems = items
                    .map((item, index) => this.transformGemItem(item, typeName, index))
                    .filter(item => item !== null);
                if (processedItems.length > 0) {
                    processedGems[typeName] = processedItems;
                }
            });
            // Register session-specific types with the registry
            gemTypeRegistry.registerSessionTypes(processedGems);
            return processedGems;
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error processing session gems:`, error);
            throw new GemsServiceError('Failed to process session gems', error);
        }
    }
    /**
     * Transform a single gem item to standardized format
     * @param item - Raw gem item
     * @param typeName - Gem type name
     * @param index - Item index for fallback ID
     * @returns Transformed gem item or null if invalid
     */
    static transformGemItem(item, typeName, index) {
        try {
            if (!item || typeof item !== 'object') {
                console.warn(`${this.DEBUG_PREFIX} Invalid item in ${typeName}[${index}]:`, item);
                return null;
            }
            // Ensure item has an ID
            const id = item.id || item.Id || `${typeName}_${index}`;
            // Transform title field if needed
            const text = item.title || item.text || item.name || 'Untitled';
            // Create standardized item with all original properties preserved
            const transformedItem = {
                ...item,
                id,
                text,
                title: text // Keep both for compatibility
            };
            return transformedItem;
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error transforming item ${typeName}[${index}]:`, error);
            return null;
        }
    }
    /**
     * Discover gem types from session data
     * @param gems - Session gems data
     * @returns Array of discovered gem types with configurations
     */
    static discoverGemTypes(gems) {
        try {
            return gemTypeRegistry.discoverTypes(gems);
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error discovering gem types:`, error);
            throw new GemsServiceError('Failed to discover gem types', error);
        }
    }
    /**
     * Get gems by type
     * @param gems - Session gems data
     * @param typeName - Type name to retrieve
     * @returns Array of gem items for the type
     */
    static getGemsByType(gems, typeName) {
        try {
            if (!gems || !typeName) {
                return [];
            }
            const items = gems[typeName] || [];
            return items;
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error getting gems for type ${typeName}:`, error);
            return [];
        }
    }
    /**
     * Count total gems across all types
     * @param gems - Session gems data
     * @returns Total count of gems
     */
    static countTotalGems(gems) {
        try {
            if (!gems)
                return 0;
            const total = Object.values(gems).reduce((sum, items) => {
                return sum + (Array.isArray(items) ? items.length : 0);
            }, 0);
            return total;
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error counting gems:`, error);
            return 0;
        }
    }
    /**
     * Get sorted gem types by priority and count
     * @param gems - Session gems data
     * @returns Sorted array of type names
     */
    static getSortedGemTypes(gems) {
        try {
            if (!gems)
                return [];
            const discoveredTypes = this.discoverGemTypes(gems);
            return discoveredTypes.map(type => type.name);
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error sorting gem types:`, error);
            return Object.keys(gems || {});
        }
    }
    /**
     * Filter gems by search query
     * @param gems - Session gems data
     * @param query - Search query
     * @returns Filtered gems data
     */
    static filterGems(gems, query) {
        if (!query.trim())
            return gems;
        const filteredGems = {};
        const lowerQuery = query.toLowerCase();
        Object.entries(gems).forEach(([typeName, items]) => {
            const filteredItems = items.filter((item) => {
                return this.searchInItem(item, lowerQuery);
            });
            if (filteredItems.length > 0) {
                filteredGems[typeName] = filteredItems;
            }
        });
        return filteredGems;
    }
    /**
     * Search within a gem item
     * @param item - Gem item to search
     * @param query - Lowercase search query
     * @returns True if item matches query
     */
    static searchInItem(item, query) {
        const searchFields = [
            ...FIELD_MAPPINGS.title,
            ...FIELD_MAPPINGS.description,
            ...FIELD_MAPPINGS.action,
            'quote'
        ];
        return searchFields.some(field => {
            const value = item[field];
            return value && typeof value === 'string' && value.toLowerCase().includes(query);
        });
    }
    /**
     * Export gems to different formats
     * @param gems - Session gems data
     * @param format - Export format
     * @returns Exported data string
     */
    static exportGems(gems, format = 'json') {
        try {
            switch (format) {
                case 'json':
                    return JSON.stringify(gems, null, 2);
                case 'csv':
                    return this.exportToCSV(gems);
                case 'markdown':
                    return this.exportToMarkdown(gems);
                default:
                    throw new Error(`Unsupported export format: ${format}`);
            }
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Error exporting gems:`, error);
            throw new GemsServiceError(`Failed to export gems as ${format}`, error);
        }
    }
    /**
     * Export gems to CSV format
     */
    static exportToCSV(gems) {
        const rows = ['Type,Title,Summary,Next Step,Quote,Citation'];
        Object.entries(gems).forEach(([typeName, items]) => {
            items.forEach((item) => {
                const row = [
                    typeName,
                    item.text || item.title || '',
                    item.summary || '',
                    item.next_step || '',
                    item.quote || '',
                    item.citation_id || ''
                ].map(field => `"${(field || '').replace(/"/g, '""')}"`);
                rows.push(row.join(','));
            });
        });
        return rows.join('\n');
    }
    /**
     * Export gems to Markdown format
     */
    static exportToMarkdown(gems) {
        const sections = ['# Session Gems\n'];
        Object.entries(gems).forEach(([typeName, items]) => {
            sections.push(`## ${typeName} (${items.length} items)\n`);
            items.forEach((item, index) => {
                sections.push(`### ${index + 1}. ${item.text || item.title || 'Untitled'}\n`);
                if (item.summary) {
                    sections.push(`**Summary:** ${item.summary}\n`);
                }
                if (item.quote) {
                    sections.push(`> ${item.quote}\n`);
                }
                if (item.next_step) {
                    sections.push(`**Next Step:** ${item.next_step}\n`);
                }
                sections.push('---\n');
            });
        });
        return sections.join('\n');
    }
}
export default GemsService;
